import React, { useState, useEffect } from "react";
import styles from "../components/Css/ViewAll.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { useUniqueAudiosByCategory } from "../../hooks/useChapters";
import Footer from "../../common/UI/Footer";
import Loading from "../../common/HousGlass Loader/Loading";

const ReadMoreNonFictional = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Truncate text utility function
  const truncateText = (text, limit) => {
    if (text && text.length > limit) {
      return text.substring(0, limit) + " ...";
    }
    return text;
  };

  // Use custom hook for audio data
  const { 
    audios, 
    isLoading, 
    currentPage: apiCurrentPage, 
    totalPages, 
    refetchAudios 
  } = useUniqueAudiosByCategory(name, 'Audio', 'Audio Book', 1, 12);
  
  // Device detection for responsive design
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    // Check if screen width is less than 768px (mobile)
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setScreenWidth(width);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Set body background to black
    document.body.style.backgroundColor = "#000000";
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
      // Reset body background when component unmounts
      document.body.style.backgroundColor = "";
    };
  }, []);
  
  const handlePageChange = (newPage) => {
    refetchAudios(newPage, 12);
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };
  
  // Handle navigation when card is clicked
  const handleCardClick = (audioItem) => {
    if (!audioItem?.series_slug) return;
    navigate(`/audio-play/${audioItem.audio_slug}`);
  };

  // Hero carousel navigation
  const nextSlide = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!audios || !audios.length) return;
    
    // Using max 3 items in hero section
    const heroItems = audios.slice(0, 3);
    setActiveIndex((prevIndex) => (prevIndex + 1) % heroItems.length);
  };

  const prevSlide = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!audios || !audios.length) return;
    
    // Using max 3 items in hero section
    const heroItems = audios.slice(0, 3);
    setActiveIndex((prevIndex) => (prevIndex - 1 + heroItems.length) % heroItems.length);
  };

  const setSlide = (index, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveIndex(index);
  };
  
  // Select appropriate image source based on screen size
  const getImageSource = (item) => {
    if (!item) return '';
    
    // Default to cover image if available
    if (item.cover_image_url) return item.cover_image_url;
    
    // Otherwise use device-specific image
    return screenWidth < 768 ? item.mobile_image_url : item.computer_image_url;
  };
  
  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  // Get the first 3 audio items for the hero section (Netflix-style)
  const heroItems = audios && audios.length > 0 ? audios.slice(0, 3) : [];
  
  // The rest of the items for the grid view
  const gridItems = audios && audios.length > 0 ? audios : [];
  
  return (
    <>
      <div className={styles.container}>
        {/* Hero Section - Netflix Style */}
        {heroItems.length > 0 && (
          <div className={styles.heroSection}>
            <div className={styles.heroCarousel}>
              <div 
                className={styles.heroSlideTrack} 
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
              >
                {heroItems.map((item, index) => (
                  <div key={item.audio_id} className={styles.heroSlide}>
                    <div 
                      className={styles.heroContent}
                      onClick={() => handleCardClick(item)}
                    >
                      <div className={styles.heroImageContainer}>
                        <img
                          src={getImageSource(item)}
                          alt={item.audio_title}
                          className={styles.heroImage}
                        />
                      </div>
                      <div className={styles.heroDetails}>
                        <h2 className={styles.heroTitle}>
                          {truncateText(item.audio_title, 30)}
                        </h2>
                        <p className={styles.heroDescription}>
                          {truncateText(item.audio_description || "No description available", 100)}
                        </p>
                        <button 
                          className={styles.listenButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(item);
                          }}
                        >
                          Listen Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className={styles.heroControls}>
                <button 
                  onClick={prevSlide} 
                  className={styles.heroButton}
                  aria-label="Previous"
                >
                  &#10094;
                </button>
                <button 
                  onClick={nextSlide} 
                  className={styles.heroButton}
                  aria-label="Next"
                >
                  &#10095;
                </button>
              </div>
              
              <div className={styles.carouselIndicators}>
                {heroItems.map((_, index) => (
                  <span
                    key={index}
                    className={`${styles.indicator} ${index === activeIndex ? styles.activeIndicator : ''}`}
                    onClick={(e) => setSlide(index, e)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Category Title */}
        <div className={styles.header}>
          <h1>{name} Collection</h1>
        </div>
        
        {/* Books Grid */}
        <div className={styles.booksGrid}>
          {gridItems.map((audioItem) => (
            <div
              key={audioItem.audio_id}
              className={styles.bookCard}
              onClick={() => handleCardClick(audioItem)}
            >
              <div className={styles.bookImageContainer}>
                <img
                  src={getImageSource(audioItem)}
                  alt={audioItem.audio_title}
                  className={styles.bookImage}
                  loading="lazy"
                />
                {audioItem.is_premium && (
                  <span className={styles.premiumBadge}>Premium</span>
                )}
              </div>
              <div className={styles.bookInfo}>
                <h3 className={styles.bookTitle}>{truncateText(audioItem.audio_title, 20)}</h3>
                <p className={styles.chapterCount}>
                  {audioItem.chapter_count || 0} {parseInt(audioItem.chapter_count) === 1 ? 'Chapter' : 'Chapters'}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* No results message */}
        {!audios || audios.length === 0 ? (
          <div className={styles.noResults}>
            No audio books found in this category
          </div>
        ) : null}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => handlePageChange(apiCurrentPage - 1)}
              disabled={apiCurrentPage === 1}
              className={styles.paginationButton}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {apiCurrentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(apiCurrentPage + 1)}
              disabled={apiCurrentPage === totalPages}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        )}
      </div>
      <Footer/>
    </>
  );
};

export default ReadMoreNonFictional;